import * as React from 'react';
import { useTheme } from '@emotion/react';
import { AnimatePresence, motion } from 'motion/react';
import {
  FEATURE_CATEGORIES,
  FEATURE_SYSTEMS,
  FEATURE_NODE_LABELS,
  FeatureNodeType,
  PLATFORM_FEATURES
} from '../../../../data/features';
import { STORY_TIMELINE } from '../story';

const foundation = ['oxide', 'cli', 'backend', 'cloudcode', 'runtime', 'sector', 'override'] as const;
const categoryTypes = {
  development: FeatureNodeType.TOOL,
  data: FeatureNodeType.CLOUD_SERVICE,
  automation: FeatureNodeType.CLOUD_SERVICE,
  runtime: FeatureNodeType.RUNTIME,
  devices: FeatureNodeType.RUNTIME,
  administration: FeatureNodeType.INTERFACE
};
const capabilityPool = [
  ...FEATURE_SYSTEMS.map((system) => ({
    id: system.id,
    name: system.name,
    label: FEATURE_NODE_LABELS[system.type],
    type: system.type
  })),
  ...PLATFORM_FEATURES.map((feature) => ({
    id: feature.id,
    name: feature.name,
    label: FEATURE_CATEGORIES.find((category) => category.id === feature.category)!.name,
    type: categoryTypes[feature.category]
  }))
];
const capabilities = [
  'photonsync',
  'embedded-power-bi-dashboards',
  'custom-policies',
  'cloudcode-queues',
  'translations',
  'hardware-barcode-scanning',
  'organization-sso',
  '3d-model-display',
  'data-change-triggers',
  'source-drafts-and-history',
  'push-notifications',
  'nfc',
  'ai-development',
  'audit-logs',
  'scheduled-execution',
  'app-packages',
  'backend-api',
  'voice-control',
  'bluetooth-low-energy-ble',
  'live-co-editing',
  'cloudcode-http',
  'custom-branded-builds',
  'data-model-relationships',
  'deep-linking',
  'gps-capture-and-tracking',
  'pdf-reports-in-cloudcode',
  'batch-operations',
  'themes-and-app-styling',
  'log-search',
  'external-npm-packages'
].map((id) => capabilityPool.find((feature) => feature.id === id)!);

export const KeepBuildingArtwork: React.FC<{ elapsed: number; reducedMotion: boolean; running: boolean }> = ({
  elapsed,
  reducedMotion,
  running
}) => {
  const [tiles, setTiles] = React.useState(capabilities);
  const cursor = React.useRef(0);
  const cycling = running && !reducedMotion && elapsed >= STORY_TIMELINE.finale.cycleStartMs;
  React.useEffect(() => {
    if (!cycling) return;
    const timer = window.setInterval(() => {
      const turn = cursor.current++;
      setTiles((current) => {
        const next = [...current];
        const candidate = capabilityPool
          .slice(turn % capabilityPool.length)
          .concat(capabilityPool)
          .find((item) => !current.some((tile) => tile.name === item.name))!;
        next[(turn * 7) % current.length] = candidate;
        return next;
      });
    }, 480);
    return () => window.clearInterval(timer);
  }, [cycling]);
  const theme = useTheme();
  const colors = theme.featureGraph;
  const progress = (start: number, duration: number) =>
    reducedMotion ? 1 : Math.max(0, Math.min(1, (elapsed - start) / duration));
  const foundationProgress = progress(900, 900);

  return (
    <g>
      <g opacity={foundationProgress}>
        <text x="450" y="-105" textAnchor="middle" fill={theme.text.paragraph} fontSize="13" letterSpacing="3">
          THE FOUNDATION YOU ALREADY KNOW
        </text>
        {foundation.map((id, index) => {
          const system = FEATURE_SYSTEMS.find((system) => system.id === id)!;
          const color = colors[system.type];
          return (
            <g key={id} transform={`translate(${-410 + index * 250} -70)`}>
              <rect width="220" height="76" rx="14" fill={color.background} stroke={color.border} strokeWidth="1.5" />
              <circle cx="23" cy="38" r="4" fill={color.accent} />
              <text x="40" y="44" fill={theme.text.heading} fontSize="18" fontWeight="500">
                {system.name}
              </text>
            </g>
          );
        })}
        <path
          d="M450 26 V64 M-305 90 V64 H1195 V90 M-5 90 V64 M295 90 V64 M595 90 V64 M895 90 V64"
          fill="none"
          stroke={colors.feature.border}
          strokeWidth="1.5"
        />
      </g>
      {tiles.map((feature, index) => {
        const column = index % 6;
        const row = Math.floor(index / 6);
        const reveal = progress(STORY_TIMELINE.finale.revealStartMs + row * 230 + Math.abs(column - 2.5) * 100, 850);
        const eased = reveal * reveal * (3 - 2 * reveal);
        const x = -440 + column * 300;
        const y = 115 + row * 143;
        const color = colors[feature.type];
        const lines = feature.name.split(' ').reduce<string[]>((lines, word) => {
          const last = lines.length - 1;
          if (last < 0 || `${lines[last]} ${word}`.length > 29) lines.push(word);
          else lines[last] += ` ${word}`;
          return lines;
        }, []);
        return (
          <g key={index} opacity={reveal} transform={`translate(${450 + (x - 450) * eased} ${y - 35 * (1 - eased)})`}>
            <AnimatePresence initial={false} mode="wait">
              <motion.g
                key={feature.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: reducedMotion ? 0 : 0.9, ease: 'easeInOut' }}
              >
                <rect
                  width="278"
                  height="110"
                  rx="16"
                  fill={color.background}
                  stroke={color.border}
                  strokeWidth="1.5"
                />
                <rect x="18" y="19" width="22" height="22" rx="6" fill={color.accent} opacity="0.16" />
                <path d="M25 30 H33 M29 26 V34" fill="none" stroke={color.accent} strokeWidth="1.5" />
                <text x="50" y="34" fill={color.accent} fontSize="10" letterSpacing="0.8">
                  {feature.label.toUpperCase()}
                </text>
                <text
                  x="18"
                  y={lines.length > 1 ? 66 : 77}
                  fill={theme.text.heading}
                  fontSize={lines.length > 1 ? 15 : 17}
                  fontWeight="500"
                >
                  {lines.map((line, index) => (
                    <tspan key={index} x="18" dy={index === 0 ? 0 : 18}>
                      {line}
                    </tspan>
                  ))}
                </text>
              </motion.g>
            </AnimatePresence>
          </g>
        );
      })}
      <text
        x="450"
        y="845"
        opacity={progress(2400, 700)}
        textAnchor="middle"
        fill={theme.text.paragraph}
        fontSize="14"
        letterSpacing="2"
      >
        MORE TO EXPLORE. MORE TO BUILD.
      </text>
    </g>
  );
};
